import { apiService } from '../api/api-client';
import type { HistoryPageParams } from './mtm-runs-service';
import type {
  PaginatedResponse,
  RepoMarginCall,
  RepoMarginCallBalance,
  RepoMarginCallBook,
  RepoMarginCallPreview,
  RepoMarginCallPreviewRequest,
  RepoMarginCallRequest,
  RepoMarginCallTrade,
} from '@/types';

/**
 * Page size requested for one counterparty's posting history (LR-479), read whole by the cash-pool
 * drawer. The server pages at 100 and caps ?limit at 10 000 (BlotterLimitOffsetPagination, LR-546),
 * so this is honoured as-is. The all-counterparty History feed pages instead (`getHistoryPage`).
 */
export const REPO_MC_HISTORY_LIMIT = 500;

/**
 * Repo / Reverse Repo margin call (LR-385) — per-counterparty cash-pool netting.
 *
 * Repo contracts are NOT repriced via /positions/bulk_mark/; their collateral is a
 * per-counterparty margin call: sum each trade's (current MV − booking value), net
 * against the running cash pool, and post the difference (Due to us / Due to cp).
 */
export const repoMarginCallService = {
  /** GET /repo-margin-calls/preview/ — computed call (no write). */
  async getPreview(
    counterpartyName: string,
    currency: string
  ): Promise<RepoMarginCallPreview> {
    return apiService.get<RepoMarginCallPreview>(
      '/repo-margin-calls/preview/',
      {
        params: { counterparty_name: counterpartyName, currency },
      }
    );
  },

  /**
   * POST /repo-margin-calls/preview/ — authoritative subset/override preview (LR-548). Same read-only
   * computation as the GET, but the body carries `position_ids` (subset) + `price_overrides`. Used to
   * prefill the Post dialog with the net-of-pool amount/direction for exactly the selected rows.
   */
  async previewSubset(
    request: RepoMarginCallPreviewRequest
  ): Promise<RepoMarginCallPreview> {
    return apiService.post<RepoMarginCallPreview>(
      '/repo-margin-calls/preview/',
      request
    );
  },

  /** POST /repo-margin-calls/ — post a margin call; returns the persisted record. */
  async postMarginCall(
    request: RepoMarginCallRequest
  ): Promise<RepoMarginCall> {
    return apiService.post<RepoMarginCall>('/repo-margin-calls/', request);
  },

  /** GET /repo-margin-calls/balance/ — current running cash-pool balance of a counterparty's repo book. */
  async getBalance(counterpartyName: string): Promise<RepoMarginCallBalance> {
    return apiService.get<RepoMarginCallBalance>(
      '/repo-margin-calls/balance/',
      { params: { counterparty_name: counterpartyName } }
    );
  },

  /**
   * GET /repo-margin-calls/ — posted-call history, newest-first (LR-479), for the cash-pool
   * drawer: one capped page of a counterparty's calls.
   *
   * LR-565: rows are summary-only. The frozen per-trade breakdown used to ride along here, which
   * put this feed at 4.2 MB for five rows — enough that the drawer's request never finished inside
   * a UI-test window. Use `getLines` for the drill-down instead.
   */
  async getHistory(
    counterpartyName?: string,
    currency?: string
  ): Promise<RepoMarginCall[]> {
    const params: Record<string, unknown> = { limit: REPO_MC_HISTORY_LIMIT };
    if (counterpartyName) params.counterparty_name = counterpartyName;
    if (currency) params.currency = currency;
    const page = await apiService.getPaginated<RepoMarginCall>(
      '/repo-margin-calls/',
      params
    );
    return page.results;
  },

  /** GET /repo-margin-calls/: one page of the posting history, newest-first, narrowed by the History filters. */
  async getHistoryPage(params: HistoryPageParams): Promise<PaginatedResponse<RepoMarginCall>> {
    return apiService.getPaginated<RepoMarginCall>('/repo-margin-calls/', { ...params });
  },

  /** GET /repo-margin-calls/by-counterparty/: one page of (counterparty, currency) books. */
  async getBooksPage(params: HistoryPageParams): Promise<PaginatedResponse<RepoMarginCallBook>> {
    return apiService.getPaginated<RepoMarginCallBook>('/repo-margin-calls/by-counterparty/', {
      ...params,
    });
  },

  /** GET /repo-margin-calls/{id}/: one posted call (a deep link older than the loaded pages). */
  async getCall(callId: number): Promise<RepoMarginCall> {
    return apiService.get<RepoMarginCall>(`/repo-margin-calls/${callId}/`);
  },

  /**
   * GET /repo-margin-calls/{id}/lines/ — the contributing repo trades frozen at call time
   * (LR-565), for the ledger's expanded row. Plain list, no pagination envelope — mirrors
   * `mtmRunsService.getRunMarks`.
   */
  async getLines(callId: number): Promise<RepoMarginCallTrade[]> {
    return apiService.get<RepoMarginCallTrade[]>(
      `/repo-margin-calls/${callId}/lines/`
    );
  },
};
